import { useState, useEffect, useCallback } from 'react';
import { AppUser } from '../types';
import { PropertyComment } from '../types/propertyComment';
import { isCommercialProfile } from '../utils/commercialRoleHelper';
import {
  fetchPropertyComments,
  createPropertyComment,
  updatePropertyComment,
  deletePropertyComment,
} from '../services/propertyCommentsService';

export const usePropertyComments = (propertyCode: string, appUser: AppUser | null) => {
  const [comments, setComments] = useState<PropertyComment[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [editingComment, setEditingComment] = useState<PropertyComment | null>(null);

  const canAccess = isCommercialProfile(appUser?.role);

  const loadComments = useCallback(async () => {
    if (!canAccess || !propertyCode) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchPropertyComments(propertyCode, appUser);
      setComments(data);
    } catch (err: any) {
      setError(err.message || 'No se pudieron cargar los comentarios.');
    } finally {
      setLoading(false);
    }
  }, [propertyCode, appUser, canAccess]);

  useEffect(() => {
    loadComments();
  }, [loadComments]);

  const addComment = async (content: string): Promise<boolean> => {
    if (!canAccess || !content.trim()) return false;
    setSubmitting(true);
    setError(null);
    try {
      const newComment = await createPropertyComment(propertyCode, content, appUser);
      setComments((prev) => [newComment, ...prev]);
      window.dispatchEvent(new CustomEvent('property-comment-updated', { detail: { propertyCode, delta: 1 } }));
      return true;
    } catch (err: any) {
      setError(err.message || 'Error al publicar comentario.');
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const updateComment = async (content: string): Promise<boolean> => {
    if (!editingComment || !content.trim()) return false;
    setSubmitting(true);
    setError(null);
    try {
      const updated = await updatePropertyComment(editingComment.id, content, appUser);
      setComments((prev) =>
        prev.map((c) => (c.id === updated.id ? { ...c, content: updated.content, updatedAt: updated.updatedAt } : c))
      );
      setEditingComment(null);
      return true;
    } catch (err: any) {
      setError(err.message || 'Error al modificar comentario.');
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const removeComment = async (commentId: string): Promise<boolean> => {
    if (!canAccess) return false;
    setError(null);
    try {
      await deletePropertyComment(commentId, appUser);
      setComments((prev) => prev.filter((c) => c.id !== commentId));
      if (editingComment?.id === commentId) setEditingComment(null);
      window.dispatchEvent(new CustomEvent('property-comment-updated', { detail: { propertyCode, delta: -1 } }));
      return true;
    } catch (err: any) {
      setError(err.message || 'Error al eliminar comentario.');
      return false;
    }
  };

  return {
    comments,
    loading,
    submitting,
    error,
    editingComment,
    canAccess,
    addComment,
    updateComment,
    removeComment,
    startEditing: setEditingComment,
    cancelEditing: () => setEditingComment(null),
    reloadComments: loadComments,
  };
};
